import { twMerge } from "tailwind-merge";
import {
  CommonCodeGroupType,
  CreateWelfareContentAsAdminMutationVariables,
  GetBokjiroWelfareContentsAsAdminQuery,
  useCreateWelfareContentAsAdminMutation,
  useGetCommonCodeGroupsByTypeAsAdminQuery,
} from "../../../lib/apollo/graphql/generated";
import { FETCH_SIZE } from "../../../utilities/constants";
import { getTotalPages } from "../../../utilities/pagination";
import BoxWithShadow from "../../common/components/BoxWithShadow";
import Table from "../../common/components/Table";
import { CloudArrowUpIcon } from "@heroicons/react/24/outline";

interface BokjiroListProps {
  isLoading: boolean;
  data?: GetBokjiroWelfareContentsAsAdminQuery;
  page: number;
  onPageChange: (newPage: number) => void;
}

export default function BokjiroList({
  data,
  isLoading = false,
  page,
  onPageChange,
}: BokjiroListProps) {
  const welfares = data?.getBokjiroWelfareContentsAsAdmin?.edges || [];
  const pageInfo = data?.getBokjiroWelfareContentsAsAdmin?.pageInfo;
  const totalPages = getTotalPages(pageInfo?.totalCount, FETCH_SIZE);

  const [registerContentToApp, { loading: isCreating }] =
    useCreateWelfareContentAsAdminMutation({
      onCompleted: () => {
        alert("복지정보를 앱에 등록했습니다.");
      },
      refetchQueries: ["GetBokjiroWelfareContentsAsAdmin"],
    });

  const { data: statusData } = useGetCommonCodeGroupsByTypeAsAdminQuery({
    variables: {
      types: [CommonCodeGroupType.Status],
    },
  });

  const activeStatusId =
    statusData?.getCommonCodeGroupsByTypeAsAdmin[0]?.activeCommonCodes?.find(
      (status) => status.code === "ACTIVE"
    )?.id;

  const onRegister = (
    event: React.MouseEvent,
    variables: CreateWelfareContentAsAdminMutationVariables
  ) => {
    event.stopPropagation();

    if (isCreating || !window.confirm("해당 내용을 앱에 등록할까요?")) {
      return;
    }

    registerContentToApp({
      variables,
    });
  };

  return (
    <BoxWithShadow className="gap-3">
      <Table.Wrapper>
        <Table.Header
          tabs={[
            { label: "제목", className: "w-1/5 flex-shrink-0" },
            { label: "설명", className: "w-2/5 flex-shrink-0" },
            { label: "제공유형" },
            { label: "문의처" },
            { label: "앱 등록", className: "w-[100px] flex-shrink-0" },
          ]}
        />
        <Table.Body isLoading={isLoading} isEmpty={!welfares?.length}>
          {welfares?.map((welfare) => {
            return (
              <Table.Row
                onClick={() => {
                  if (welfare?.detailPageUrl) {
                    window.open(
                      welfare.detailPageUrl,
                      "_blank",
                      "noopener,noreferrer"
                    );
                  } else {
                    alert("복지정보 상세페이지 주소를 찾을 수 없습니다");
                  }
                }}
              >
                <Table.Column className="line-clamp-2 flex w-1/5 flex-shrink-0 text-wrap font-semibold">
                  {welfare?.title}
                </Table.Column>
                <Table.Column className="line-clamp-3 w-2/5 flex-shrink-0 justify-start text-wrap">
                  {welfare?.description}
                </Table.Column>
                <Table.Column className="line-clamp-2 flex text-ellipsis text-wrap">
                  {welfare?.supportType}
                </Table.Column>
                <Table.Column className="line-clamp-3 flex text-ellipsis whitespace-pre text-wrap text-center">
                  {welfare?.contactInformation}
                  {welfare?.inquiryPhone && ` (${welfare.inquiryPhone})`}
                </Table.Column>
                <Table.Column className="w-[100px] flex-shrink-0">
                  <div
                    onClick={(event) => {
                      event.stopPropagation();

                      if (welfare.isInserted) {
                        return;
                      }

                      onRegister(event, {
                        title: welfare.title,
                        description: welfare.description,
                        detailPageUrl: welfare.detailPageUrl,
                        contactInformation: welfare.contactInformation,
                        inquiryPhone: welfare.inquiryPhone,
                        welfareTypeId: welfare.welfareTypeId,
                        welfareServiceId: welfare.welfareServiceId,
                        welfareCategoryIds: welfare.welfareCategoryIds,
                        welfareFamilyTypeIds: welfare.welfareFamilyTypeIds,
                        welfareLifeCycleIds: welfare.welfareLifeCycleIds,
                        statusId: activeStatusId!,
                      });
                    }}
                    className="group flex h-full w-full items-center justify-center"
                  >
                    <CloudArrowUpIcon
                      className={twMerge(
                        "w-6",
                        welfare.isInserted
                          ? "text-green-600"
                          : "cursor-pointer text-zinc-300 transition-colors duration-300 group-hover:text-green-500"
                      )}
                    />
                  </div>
                </Table.Column>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table.Wrapper>
      <Table.Pagination
        page={page}
        total={totalPages}
        onPageChange={onPageChange}
      />
    </BoxWithShadow>
  );
}
